import { useState, useEffect } from "react";
import { BookOpen, ChevronDown, BookMarked, CheckCircle2, Circle, ArrowLeft, Star } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/bookReading.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

function getUserId() {
  const stored = localStorage.getItem("userId") || localStorage.getItem("user_id");
  if (stored) return parseInt(stored, 10);
  try {
    const parsed = JSON.parse(localStorage.getItem("user") || "{}");
    return parsed?.id || parsed?.userId || null;
  } catch {
    return null;
  }
}

const GENRE_LABELS = { fiction: "Fiction", "fairy-tales": "Fairy Tales" };

export default function BookReadingPage() {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const userId = getUserId();

  useEffect(() => { document.title = "Book Reading — Owllet Reading Quest"; }, []);

  const [catalogue, setCatalogue] = useState({ books: [], bookInfo: {} });
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);
  const [selectedBookId, setSelectedBookId] = useState("");
  const [progress, setProgress] = useState({ completedChapters: [], totalChapters: 0 });
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    apiFetch(`/api/books/catalogue`)
      .then((r) => r.json())
      .then((data) => {
        setCatalogue({ books: data.books || [], bookInfo: data.bookInfo || {} });
        setLoadingCatalogue(false);
        if (navState?.preBookId) setSelectedBookId(navState.preBookId);
      })
      .catch(() => setLoadingCatalogue(false));
  }, []);

  // Deduplicated book list
  const uniqueBooks = catalogue.books.reduce((acc, b) => {
    if (!acc.find((x) => x.book_id === b.book_id)) {
      acc.push({ book_id: b.book_id, title: b.title, author: b.author, genre: b.genre });
    }
    return acc;
  }, []);

  const selectedBook = uniqueBooks.find((b) => b.book_id === selectedBookId) || null;
  const selectedInfo = catalogue.bookInfo[selectedBookId] || null;
  const chaptersForBook = catalogue.books.filter((b) => b.book_id === selectedBookId);

  useEffect(() => {
    if (!selectedBookId || !userId) return;
    setLoadingProgress(true);
    apiFetch(`/api/reading/progress/${userId}/${selectedBookId}`)
      .then((r) => r.json())
      .then((data) => {
        setProgress(data);
        setLoadingProgress(false);
      })
      .catch(() => setLoadingProgress(false));
  }, [selectedBookId, userId]);

  const handleBookSelect = (bookId) => {
    setSelectedBookId(bookId);
    setDropdownOpen(false);
    setProgress({ completedChapters: [], totalChapters: 0 });
  };

  const handleReadChapter = (chapter, idx) => {
    navigate("/chapterReadingPage", {
      state: {
        bookId: selectedBookId,
        chapterId: chapter.chapter_id,
        bookTitle: selectedBook?.title || selectedBookId,
        chapterTitle: `Chapter ${idx + 1}`,
        chapterDesc: chapter.chapter_desc,
        chapterIndex: idx,
        totalChapters: chaptersForBook.length,
        allChapters: chaptersForBook,
      },
    });
  };

  const completedCount = progress.completedChapters.length;
  const totalCount = chaptersForBook.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="brp-page">
      <div className="brp-container">

        {/* Header */}
        <div className="brp-header">
          <div className="brp-header-title">
            <div className="brp-header-icon">
              <BookOpen size={22} color="#fff" />
            </div>
            <div>
              <h1>Reading Mode</h1>
              <p className="brp-header-sub">Select a book and explore it chapter by chapter</p>
            </div>
            <button className="brp-back-btn" onClick={() => navigate("/games/books")}>
              <ArrowLeft size={16} /> Back
            </button>
          </div>
        </div>

        {/* Book Selector */}
        <div className="brp-selector-wrap">
          <label id="brp-book-label" className="brp-selector-label">
            <BookMarked size={14} aria-hidden="true" /> Choose a Book
          </label>
          <div
            className="brp-dropdown"
            onKeyDown={(e) => {
              if (e.key === "Escape") { setDropdownOpen(false); }
            }}
          >
            <button
              className="brp-dropdown-btn"
              onClick={() => setDropdownOpen((o) => !o)}
              onBlur={(e) => { if (!e.currentTarget.closest(".brp-dropdown").contains(e.relatedTarget)) setDropdownOpen(false); }}
              disabled={loadingCatalogue}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              aria-labelledby="brp-book-label"
            >
              {loadingCatalogue ? (
                <span className="brp-loading-text">Loading books…</span>
              ) : selectedBook ? (
                <span className="brp-selected-book">
                  <span
                    className="brp-book-dot"
                    style={{ background: selectedInfo?.cover_accent || "#6366f1" }}
                    aria-hidden="true"
                  />
                  {selectedBook.title}
                  <span className="brp-book-author">— {selectedBook.author}</span>
                </span>
              ) : (
                <span className="brp-placeholder">Select a book to begin reading…</span>
              )}
              <ChevronDown size={16} className={`brp-chevron ${dropdownOpen ? "open" : ""}`} aria-hidden="true" />
            </button>
            {dropdownOpen && (
              <div className="brp-dropdown-list" role="listbox" aria-labelledby="brp-book-label">
                {uniqueBooks.map((b) => {
                  const info = catalogue.bookInfo[b.book_id];
                  return (
                    <button
                      key={b.book_id}
                      role="option"
                      aria-selected={b.book_id === selectedBookId}
                      className={`brp-dropdown-item ${b.book_id === selectedBookId ? "active" : ""}`}
                      onClick={() => { handleBookSelect(b.book_id); setDropdownOpen(false); }}
                    >
                      <span
                        className="brp-book-dot"
                        style={{ background: info?.cover_accent || "#6366f1" }}
                        aria-hidden="true"
                      />
                      <span className="brp-item-title">{b.title}</span>
                      <span className="brp-item-author">{b.author}</span>
                      <span className="brp-item-genre">{GENRE_LABELS[b.genre] || b.genre}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Book Info Card */}
        {selectedBook && (
          <div className="brp-book-card">

            {/* Cover + Meta */}
            <div className="brp-book-top">
              <div
                className="brp-cover"
                style={{ background: `linear-gradient(145deg, ${selectedInfo?.cover_accent || "#6366f1"}, ${selectedInfo?.cover_accent || "#4f46e5"}cc)` }}
              >
                <img
                  src={`${API_BASE}/images/book-covers/${selectedBookId}.jpg`}
                  alt={selectedBook.title}
                  className="brp-cover-img"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <div className="brp-cover-title">{selectedBook.title}</div>
                <div className="brp-cover-author">{selectedBook.author}</div>
              </div>

              <div className="brp-book-meta">
                <div className="brp-meta-badges">
                  <span className="brp-badge genre">{GENRE_LABELS[selectedBook.genre] || selectedBook.genre}</span>
                  {selectedInfo?.year && (
                    <span className="brp-badge year">{selectedInfo.year}</span>
                  )}
                  {chaptersForBook[0]?.suitable_levels && (
                    <span className="brp-badge level">
                      <Star size={11} />
                      Level {Math.min(...chaptersForBook[0].suitable_levels.map(Number))}–
                      {Math.max(...chaptersForBook.flatMap((c) => c.suitable_levels.map(Number)))}
                    </span>
                  )}
                </div>

                {selectedInfo?.summary && (
                  <p className="brp-summary">{selectedInfo.summary}</p>
                )}

                {/* Reading Progress */}
                <div className="brp-progress-section">
                  <div className="brp-progress-label">
                    <span>Reading Progress</span>
                    <span className="brp-progress-count">
                      {loadingProgress ? "…" : `${completedCount} / ${totalCount} chapters`}
                    </span>
                  </div>
                  <div className="brp-progress-track">
                    <div className="brp-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                  {completedCount === totalCount && totalCount > 0 && (
                    <p className="brp-complete-label">Book complete!</p>
                  )}
                </div>
              </div>
            </div>

            {/* Chapter List */}
            <div className="brp-chapters">
              <h3 className="brp-chapters-heading">Chapters</h3>
              <div className="brp-chapter-list">
                {chaptersForBook.map((ch, idx) => {
                  const done = progress.completedChapters.includes(ch.chapter_id);
                  return (
                    <div key={ch.chapter_id} className={`brp-chapter-row ${done ? "done" : ""}`}>
                      <div className="brp-chapter-status">
                        {done
                          ? <CheckCircle2 size={18} className="brp-done-icon" />
                          : <Circle size={18} className="brp-todo-icon" />}
                      </div>
                      <div className="brp-chapter-info">
                        <span className="brp-chapter-num">Chapter {idx + 1}</span>
                        <span className="brp-chapter-desc">{ch.chapter_desc}</span>
                      </div>
                      <button
                        className="brp-read-btn"
                        onClick={() => handleReadChapter(ch, idx)}
                      >
                        {done ? "Re-read" : idx === 0 || progress.completedChapters.includes(chaptersForBook[idx - 1]?.chapter_id) ? "Read" : "Read"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        )}

        {/* Empty state */}
        {!selectedBook && !loadingCatalogue && (
          <div className="brp-empty">
            <BookOpen size={52} className="brp-empty-icon" />
            <p>Select a book above to see details and start reading.</p>
          </div>
        )}

      </div>
    </div>
  );
}
